import { computed, makeObservable, observable, runInAction } from 'mobx';

import type {
  BlackbaudRaisersEdgeIntegration,
  IBlackbaudContactMapping,
  IBlackbaudContactMappingUpdate,
  TBlackbaudContactMappingKey,
} from './blackbaud_raisers_edge';

function isRejected(result: PromiseSettledResult<unknown>): result is PromiseRejectedResult {
  return result.status === 'rejected';
}

/**
 * Observable contact mappings for one Raiser's Edge integration. Wraps the integration's mapping
 * endpoints so add, update and remove update the list in place instead of needing a page reload.
 *
 * Every change reloads the list from the server before it settles, whether the request succeeded
 * or failed. The server saves a change before it checks the webhook subscriptions, so a request
 * that failed can still have changed the mappings, and a change can alter
 * `has_provisioning_subscriptions` on other rows.
 */
export class BlackbaudRaisersEdgeContactMappingStore {
  /**
   * The integration's mappings, or `undefined` until the first load succeeds. The array is always
   * replaced whole, never mutated, so a shallow reference is enough to notify observers.
   */
  @observable.ref public mappings: IBlackbaudContactMapping[] | undefined = undefined;

  @observable public isPending = false;

  /** The last failed load that no newer load has superseded. Cleared by the next successful load. */
  @observable public error: Error | null = null;

  private readonly integration: BlackbaudRaisersEdgeIntegration;

  /** Counts every load and change, so each load knows how recent it is. */
  private generation = 0;

  /**
   * The generation `mappings` currently reflects. A load older than it is discarded, so a reload
   * can't overwrite fresher data.
   */
  private appliedGeneration = 0;

  private pendingLoads = 0;

  private fetchPromise: Promise<void> | null = null;

  public constructor(integration: BlackbaudRaisersEdgeIntegration) {
    this.integration = integration;
    makeObservable(this);
  }

  @computed
  public get isFetched(): boolean {
    return this.mappings !== undefined;
  }

  /** Mappings with required ones first, otherwise in server order. */
  @computed
  public get sortedMappings(): IBlackbaudContactMapping[] {
    const mappings = this.mappings ?? [];
    return [
      ...mappings.filter(({ required }) => required),
      ...mappings.filter(({ required }) => !required),
    ];
  }

  /** Loads the mappings once. Later calls reuse the first load or its result. */
  public fetch(): Promise<void> {
    if (this.isFetched) {
      return Promise.resolve();
    }
    this.fetchPromise ??= this.load().finally(() => {
      this.fetchPromise = null;
    });
    return this.fetchPromise;
  }

  /** Reloads the mappings from the server. Resolves once the reload has settled; it never rejects. */
  public refresh(): Promise<void> {
    return this.load();
  }

  /**
   * Adds a mapping for each key, one request per key. The mappings the server created are kept
   * even when another key fails; the first failure is rethrown after the list has reloaded.
   */
  public async add(keys: TBlackbaudContactMappingKey[]): Promise<IBlackbaudContactMapping[]> {
    const results = await Promise.allSettled(
      keys.map((key) => this.integration.addContactMappings([key])),
    );
    const added = results.flatMap((result) => (result.status === 'fulfilled' ? result.value : []));
    const addedIds = new Set(added.map(({ id }) => id));

    this.applyChange((mappings) => [...mappings.filter(({ id }) => !addedIds.has(id)), ...added]);
    await this.refresh();

    const failure = results.find(isRejected);
    if (failure) {
      throw failure.reason;
    }
    return added;
  }

  public update(change: IBlackbaudContactMappingUpdate): Promise<IBlackbaudContactMapping> {
    return this.change(this.integration.updateContactMapping(change), (mappings, updated) =>
      mappings.map((mapping) => (mapping.id === updated.id ? updated : mapping)),
    );
  }

  public remove(id: IBlackbaudContactMapping['id']): Promise<void> {
    return this.change(this.integration.deleteContactMapping(id), (mappings) =>
      mappings.filter((mapping) => mapping.id !== id),
    );
  }

  /**
   * Awaits a change request, applies its result to the list, then reloads before settling. A
   * failed request reloads too, then rethrows.
   */
  private async change<T>(
    request: Promise<T>,
    apply: (mappings: IBlackbaudContactMapping[], result: T) => IBlackbaudContactMapping[],
  ): Promise<T> {
    let result: T;
    try {
      result = await request;
    } catch (error) {
      await this.refresh();
      throw error;
    }
    this.applyChange((mappings) => apply(mappings, result));
    await this.refresh();
    return result;
  }

  private async load(): Promise<void> {
    const generation = ++this.generation;
    this.pendingLoads += 1;
    runInAction(() => {
      this.isPending = true;
    });

    try {
      const mappings = await this.integration.getContactMappings();
      runInAction(() => {
        if (generation > this.appliedGeneration) {
          this.appliedGeneration = generation;
          this.mappings = mappings;
          this.error = null;
        }
      });
    } catch (error) {
      runInAction(() => {
        if (generation > this.appliedGeneration) {
          this.error = error instanceof Error ? error : new Error(String(error));
        }
      });
    } finally {
      this.pendingLoads -= 1;
      runInAction(() => {
        this.isPending = this.pendingLoads > 0;
      });
    }
  }

  /**
   * Applies a successful change to the list and marks every load started before it as stale.
   * Before the first load there is no list to change; the reload that follows every change
   * loads it.
   */
  private applyChange(
    change: (mappings: IBlackbaudContactMapping[]) => IBlackbaudContactMapping[],
  ): void {
    this.appliedGeneration = ++this.generation;
    const { mappings } = this;
    if (!mappings) {
      return;
    }
    runInAction(() => {
      this.mappings = change(mappings);
    });
  }
}
